import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  TransportLineBboxResponseSchema,
  NearbyTransportResponseSchema,
  TransportFeatureScopeSchema,
  TransportLayerImportTriggerResponseSchema,
  TransportLayerImportStatusSchema,
} from '../schemas/transport-layers.js';

export const transportLayers = {
  /**
   * GET /transport-lines
   *
   * Authenticated bbox-scoped endpoint for reading transport line features
   * intersecting the given viewport.
   *
   * Query params:
   *   minLon, minLat, maxLon, maxLat — bbox corners (coerced from string)
   *   scopes — optional comma-separated scope filter ('public' | 'roads')
   */
  listByBbox: endpoint(
    'GET',
    '/transport-lines',
    z.object({
      minLon: z.coerce.number(),
      minLat: z.coerce.number(),
      maxLon: z.coerce.number(),
      maxLat: z.coerce.number(),
      scopes: z
        .string()
        .transform((s) => s.split(',').map((v) => v.trim()))
        .pipe(z.array(TransportFeatureScopeSchema))
        .optional(),
    }),
    TransportLineBboxResponseSchema,
  ),

  /**
   * GET /media-units/:id/nearby-transport
   *
   * Returns nearby public transport and major-road line items for the
   * selected media unit, sorted by distance ascending.
   */
  nearbyTransport: endpoint(
    'GET',
    '/media-units/:id/nearby-transport',
    z.object({
      id: z.coerce.number().int().positive(),
    }),
    NearbyTransportResponseSchema,
  ),

  /**
   * POST /transport-layers/import/trigger
   *
   * Admin-only endpoint that enqueues a one-off transport layer import job.
   * Returns 202 Accepted with the enqueued job ID.
   * Returns 409 Conflict when a job is already active in the queue.
   */
  triggerImport: endpoint(
    'POST',
    '/transport-layers/import/trigger',
    z.object({}),
    TransportLayerImportTriggerResponseSchema,
  ),

  /**
   * GET /transport-layers/import/status
   *
   * Admin-only endpoint that returns the current transport layer import
   * job state derived from the BullMQ queue.
   */
  importStatus: endpoint(
    'GET',
    '/transport-layers/import/status',
    z.object({}),
    TransportLayerImportStatusSchema,
  ),
};
